import { F } from "pages/AppraisalManagment/constants/legacyApiKeys";

export const APPRAISAL_MAX_RATING_VALUE = 100;
export const APPRAISAL_MAX_RATING_LABEL = "Percentage (0-100%)";
export const LEGACY_MAX_RATINGS = [5, 10];
const VALID_MAX_RATINGS = [APPRAISAL_MAX_RATING_VALUE, ...LEGACY_MAX_RATINGS];

/** Setup forms always use the percentage scale. */
export const normalizeSetupMaxRating = (rating) => APPRAISAL_MAX_RATING_VALUE;

export const formatMaxRatingLabel = (rating) => {
  const value = Number(rating);
  if (value === APPRAISAL_MAX_RATING_VALUE) return APPRAISAL_MAX_RATING_LABEL;
  if (LEGACY_MAX_RATINGS.includes(value)) return APPRAISAL_MAX_RATING_LABEL;
  if (rating != null && String(rating).trim() !== "") return String(rating);
  return "—";
};

/** Max rating scales supported on appraisal progress scoring rows. */
export const isValidMaxRating = (maxRating) => VALID_MAX_RATINGS.includes(Number(maxRating));

/** Row participates in scoring when it has a supported max-rating scale. */
export const isScoringRow = (row) => isValidMaxRating(row?.rating);

/**
 * Convert obtained score on any scale (5, 10, 100) to a percentage of that scale.
 * Values may exceed 100% when obtained exceeds the scale (e.g. 127 on a 100-point scale).
 * Returns null when the score is missing or invalid.
 */
export const scoreToPercentage = (obtainedScore, maxRating) => {
  const obtained = Number(obtainedScore);
  const max = Number(maxRating);

  if (!Number.isFinite(obtained) || obtained <= 0) return null;
  if (!isValidMaxRating(max)) return null;

  return (obtained / max) * 100;
};

const parsePositiveWeight = (value) => {
  const weight = Number(value);
  return Number.isFinite(weight) && weight > 0 ? weight : 0;
};

/**
 * Section weight from setup/record metadata, or sum of question weightages as fallback.
 */
export const resolveSectionWeightage = (sectionTitle, rows = [], sectionWeightageByTitle = {}) => {
  const titleKey = String(sectionTitle || "").trim().toLowerCase();
  const fromSetup = sectionWeightageByTitle[titleKey];
  if (Number.isFinite(fromSetup) && fromSetup > 0) {
    return fromSetup;
  }

  const itemWeights = (rows || [])
    .map((row) => parsePositiveWeight(row?.weightage))
    .filter((value) => value > 0);

  if (!itemWeights.length) return 0;

  const summed = itemWeights.reduce((sum, value) => sum + value, 0);
  if (summed <= 100) return summed;

  const allEqual = itemWeights.every((value) => value === itemWeights[0]);
  return allEqual ? itemWeights[0] : summed;
};

/** Merge a setup row with saved sectionData (same rules as the progress form). */
export const mergeScoringRowWithSectionData = (row, categoryTitle, sectionData = {}) => {
  const groupTitle = row[F.titleSynonym] || categoryTitle;
  let savedData = null;

  if (sectionData && sectionData[groupTitle] && Array.isArray(sectionData[groupTitle])) {
    savedData = sectionData[groupTitle].find((cat) => {
      const currentId = row.id || row[F.setupPivotKey] || null;
      const existingId = cat.id || cat[F.setupPivotKey] || null;
      if (currentId && existingId) {
        return String(existingId) === String(currentId);
      }
      return cat.title === row.title;
    });
  }

  if (!savedData) {
    savedData = sectionData?.[row[F.setupPivotKey]] || sectionData?.[row.id];
  }

  return {
    ...row,
    rating: savedData?.rating ?? row.rating,
    weightage: savedData?.weightage ?? row.weightage,
    obtained_score: savedData?.obtained_score ?? row.obtained_score,
    comments: savedData?.comments ?? row.comments ?? "",
  };
};

/**
 * Weighted question score for one section (percentage; may exceed 100).
 * Each scored question contributes: questionPercent * (questionWeight / 100).
 */
export const calculateSectionScorePercentage = (rows = []) => {
  const scoringRows = (rows || []).filter(isScoringRow);
  if (!scoringRows.length) return null;

  let weightedSum = 0;

  scoringRows.forEach((row) => {
    const questionPercent = scoreToPercentage(row.obtained_score, row.rating);
    if (questionPercent === null) return;

    const weight = parsePositiveWeight(row.weightage);
    if (weight > 0) {
      weightedSum += questionPercent * (weight / 100);
    }
  });

  return weightedSum > 0 ? weightedSum : null;
};

/**
 * Combined appraisal summary across all scoring sections in sectionCategories.
 * Returns null when no section has a calculable score yet.
 */
export const calculateAppraisalSummaryPercentage = ({
  sectionCategories = {},
  sectionData = {},
  sectionWeightageByTitle = {},
}) => {
  const contributions = [];

  Object.keys(sectionCategories || {}).forEach((sectionTitle) => {
    if (String(sectionTitle).toLowerCase() === "overall_score") return;

    const rawRows = sectionCategories[sectionTitle] || [];
    const mergedRows = rawRows.map((row) =>
      mergeScoringRowWithSectionData(row, sectionTitle, sectionData)
    );

    if (!mergedRows.some(isScoringRow)) return;

    const sectionScore = calculateSectionScorePercentage(mergedRows);
    if (sectionScore === null) return;

    const sectionWeight = resolveSectionWeightage(
      sectionTitle,
      mergedRows,
      sectionWeightageByTitle
    );

    contributions.push({ sectionScore, sectionWeight });
  });

  if (!contributions.length) return null;

  const totalExplicitWeight = contributions.reduce(
    (sum, item) => sum + (item.sectionWeight > 0 ? item.sectionWeight : 0),
    0
  );

  if (totalExplicitWeight > 0) {
    return contributions.reduce(
      (sum, item) =>
        sum + item.sectionScore * ((item.sectionWeight > 0 ? item.sectionWeight : 0) / 100),
      0
    );
  }

  return (
    contributions.reduce((sum, item) => sum + item.sectionScore, 0) / contributions.length
  );
};

/** Display helper for the summary badge (e.g. "90" or "—"). */
export const formatSummaryPercentageDisplay = (summaryPercentage) => {
  if (summaryPercentage === null || summaryPercentage === undefined) {
    return null;
  }
  if (!Number.isFinite(summaryPercentage)) {
    return null;
  }
  return String(Math.round(summaryPercentage));
};
